import { memo } from 'react';

export type SkeletonVariant =
  | 'id'
  | 'option'
  | 'status'
  | 'price'
  | 'address'
  | 'conn'
  | 'action';

export type CellAlign = 'left' | 'center' | 'right';

export interface TableSkeletonColumn {
  label: string;
  width: string;
  align?: CellAlign;
  /** Skeleton bar style for body cells in this column. */
  variant?: SkeletonVariant;
}

interface TableSkeletonProps {
  columns: TableSkeletonColumn[];
  rows?: number;
  /** Show the spinning "Loading Data" overlay (used by Stations). */
  showLoadingOverlay?: boolean;
  /** Mirrors the table's `minWidth`: scroll instead of squeezing columns. */
  minWidth?: string;
  /** Mirrors the table's `dense`. */
  dense?: boolean;
}

const headerAlignClass = (a?: CellAlign) =>
  a === 'center' ? ' table-header-cell-center'
  : a === 'right' ? ' table-header-cell-right'
  : '';

const cellAlignClass = (a?: CellAlign) =>
  a === 'center' ? ' table-cell-center'
  : a === 'right' ? ' table-cell-right'
  : '';

export const TableSkeleton = memo(function TableSkeleton({
  columns,
  rows = 5,
  showLoadingOverlay = false,
  minWidth,
  dense,
}: TableSkeletonProps) {
  const wrapperStyle = showLoadingOverlay ? { position: 'relative' as const } : undefined;

  return (
    <div className="stations-table-wrapper" style={wrapperStyle}>
      {showLoadingOverlay && (
        <div className="loading-overlay">
          <div className="loading-spinner-ring">
            <div className="loading-spinner-bg" />
            <div className="loading-spinner-fg" />
          </div>
          <span className="loading-text">Loading Data</span>
        </div>
      )}

      <div className="stations-table-scroll">
        <table
          className={`stations-table${dense ? ' stations-table-dense' : ''}`}
          style={minWidth ? { minWidth } : undefined}
        >
          <thead>
            <tr className="table-header-row">
              {columns.map((col) => (
                <th
                  key={col.label}
                  className={`table-header-cell${headerAlignClass(col.align)}`}
                  style={{ width: col.width, color: '#94a3b8' }}
                >
                  <span>{col.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex} className="table-body-row skeleton-row">
                {columns.map((col) => {
                  const variant = col.variant ?? 'id';
                  const isAddress = variant === 'address';
                  const inlineStyle =
                    col.align === 'right'
                      ? { marginLeft: 'auto' }
                      : col.align === 'center' && variant === 'action'
                        ? { margin: '0 auto' }
                        : isAddress
                          ? { width: `${10 + (rowIndex % 3) * 2}rem` }
                          : undefined;

                  return (
                    <td
                      key={col.label}
                      className={`table-cell skeleton-cell${cellAlignClass(col.align)}`}
                    >
                      <div className={`skeleton skeleton-${variant}`} style={inlineStyle} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="skeleton skeleton-pagination-left" />
        <div className="skeleton skeleton-pagination-right" />
      </div>
    </div>
  );
});

export default TableSkeleton;
